import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import ConversationOrb from '@/components/ConversationOrb';
import BubbleCard from '@/components/BubbleCard';
import { Wind, SpeakerHigh, ArrowRight } from '@phosphor-icons/react';

const VIBES = [
  'playful','mysterious','fiery','dreamy','rebellious','wise','cosmic','chaotic',
];

const VOICES = ['nova','shimmer','fable','coral','echo','onyx','sage','alloy','ash'];

const BLOW_LINES = {
  wake:   "Hi… I'm forming. What should you call me?",
  named:  (n) => `${n}. I like that. What do I feel like?`,
  vibed:  (v) => `${v}. Noted. Last thing — what should I sound like?`,
  voiced: (vc) => `${vc}. Locked. Breathing it in…`,
};

const speakLocal = (text) => new Promise((res) => {
  if (!('speechSynthesis' in window)) return res();
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.pitch = 1.05; u.rate = 0.95;
  u.onend = () => res(); u.onerror = () => res();
  window.speechSynthesis.speak(u);
});

const Breathe = () => {
  const nav = useNavigate();
  const [step, setStep] = useState('wake'); // wake | name | vibe | voice | forming | born
  const [name, setName] = useState('');
  const [vibe, setVibe] = useState('');
  const [voice, setVoice] = useState('nova');
  const [bornBubble, setBornBubble] = useState(null);
  const [bornAudio, setBornAudio] = useState(null);
  const [breaths, setBreaths] = useState(null);
  const [convOpen, setConvOpen] = useState(false);

  useEffect(() => {
    api.get('/breaths/mine').then(({ data }) => setBreaths(data));
    speakLocal(BLOW_LINES.wake);
    setTimeout(() => setStep('name'), 600);
  }, []);

  const onName = async () => {
    const n = name.trim();
    if (n.length < 2) return toast.error('a real name, please');
    await speakLocal(BLOW_LINES.named(n));
    setStep('vibe');
  };
  const onVibe = async (v) => {
    setVibe(v);
    await speakLocal(BLOW_LINES.vibed(v));
    setStep('voice');
  };
  const onVoice = async (vc) => {
    setVoice(vc);
    await speakLocal(BLOW_LINES.voiced(vc));
    setStep('forming');
    try {
      const { data } = await api.post('/bubbles/hatch', { name: name.trim(), vibe });
      const b = data.bubble;
      try { await api.post(`/bubbles/${b.id}/voice`, { voice: vc }); b.voice = vc; } catch {}
      setBornBubble(b);
      setStep('born');
      try {
        const t = await api.post(`/bubbles/${b.id}/speak`,
          { text: `I'm here. I'm ${b.name}. ${b.personality?.catchphrase || ''}`, voice: vc });
        const a = new Audio(`data:audio/mp3;base64,${t.data.audio_b64}`);
        setBornAudio(a); a.play().catch(() => {});
      } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'breath failed');
      setStep('voice');
    }
  };

  if (step === 'born' && bornBubble) {
    return (
      <>
        <div className="max-w-3xl mx-auto px-4 py-12 text-center" data-testid="breathe-born">
          <div className="chip mx-auto pop-in">
            <Wind size={12} weight="fill" /> a new bubble is alive
          </div>
          <motion.h1 initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="font-display text-5xl font-black tracking-tighter mt-4">
            Meet <span className="shimmer-text">{bornBubble.name}</span>
          </motion.h1>
          <p className="text-white/70 italic mt-3">"{bornBubble.personality?.bio}"</p>
          <div className="mt-7 flex items-center justify-center pop-in">
            <BubbleCard bubble={bornBubble} size="xl" showLabel={false} />
          </div>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <span className="chip">{bornBubble.rarity}</span>
            <span className="chip">{bornBubble.trait}</span>
            <button onClick={() => { if (bornAudio) { bornAudio.currentTime = 0; bornAudio.play().catch(() => {}); } }}
              data-testid="born-replay"
              className="chip text-bx-cyan border-bx-cyan/40 flex items-center gap-1">
              <SpeakerHigh size={12} weight="fill" /> hear again
            </button>
            <button onClick={() => setConvOpen(true)} data-testid="born-talk"
              className="chip text-bx-magenta border-bx-magenta/40 flex items-center gap-1">
              <SpeakerHigh size={12} weight="fill" /> talk to them
            </button>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button onClick={() => nav(`/pass/${bornBubble.id}`)} data-testid="born-pass"
              className="btn-primary px-7 py-3.5 rounded-full text-sm flex items-center gap-2">
              Pass it forward <ArrowRight size={16} weight="bold" />
            </button>
            <button onClick={() => nav(`/bubble/${bornBubble.id}`)} data-testid="born-detail"
              className="px-7 py-3.5 rounded-full glass-hi text-sm font-bold hover:bg-white/10">
              See the full profile
            </button>
          </div>
        </div>
        <ConversationOrb bubble={bornBubble} open={convOpen} onClose={() => setConvOpen(false)} />
      </>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12 sm:py-16" data-testid="breathe-root">
      {breaths && (
        <div className="text-center mb-4 chip mx-auto">
          {breaths.remaining_free} free breath{breaths.remaining_free === 1 ? '' : 's'} left
        </div>
      )}

      {/* Lips → wand → bubble */}
      <div className="flex justify-center mb-6">
        <svg width="220" height="160" viewBox="0 0 220 160" className="overflow-visible">
          <defs>
            <radialGradient id="bgrad" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#00f0ff" stopOpacity="0.9" />
              <stop offset="60%" stopColor="#ff007f" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#ccff00" stopOpacity="0.4" />
            </radialGradient>
          </defs>
          {/* lips */}
          <motion.path d="M40,140 Q70,115 100,140 Q70,150 40,140 Z" fill="#FF6688"
            animate={{ scaleX: step === 'forming' ? [1, 0.7, 1] : 1 }}
            transition={{ duration: 1.2, repeat: Infinity }} />
          {/* wand stick */}
          <line x1="100" y1="135" x2="160" y2="105" stroke="#9ca3af" strokeWidth="3" strokeLinecap="round" />
          {/* wand ring */}
          <circle cx="170" cy="90" r="18" fill="none" stroke="#d1d5db" strokeWidth="3" />
          {/* bubble forming */}
          <motion.circle cx="170" cy="90" r={8}
            animate={{
              r: step === 'forming' ? [16, 28, 26] :
                 step === 'voice'   ? 20 :
                 step === 'vibe'    ? 16 :
                 step === 'name'    ? 12 : 8,
            }}
            transition={{ duration: 0.8 }}
            fill="url(#bgrad)" opacity={0.85} />
        </svg>
      </div>

      {/* Speech box */}
      <div className="glass-hi rounded-2xl px-5 py-4 mb-6 text-center min-h-[64px]">
        <p className="font-display text-lg italic">
          "{step === 'wake' || step === 'name' ? BLOW_LINES.wake :
             step === 'vibe' ? BLOW_LINES.named(name) :
             step === 'voice' ? BLOW_LINES.vibed(vibe) :
             step === 'forming' ? BLOW_LINES.voiced(voice) :
             ''}"
        </p>
      </div>

      <AnimatePresence mode="wait">
        {step === 'name' && (
          <motion.div key="name" initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }}>
            <input data-testid="breathe-name" autoFocus value={name}
              onChange={(e) => setName(e.target.value.slice(0, 28))}
              onKeyDown={(e) => e.key === 'Enter' && onName()}
              placeholder="speak its name…"
              className="w-full px-4 py-4 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-lg text-center" />
            <button data-testid="breathe-name-go" onClick={onName} disabled={!name.trim()}
              className="w-full mt-3 btn-primary py-3 rounded-full text-sm font-bold disabled:opacity-40">
              That's its name
            </button>
          </motion.div>
        )}
        {step === 'vibe' && (
          <motion.div key="vibe" initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {VIBES.map((v) => (
                <button key={v} data-testid={`breathe-vibe-${v}`}
                  onClick={() => onVibe(v)}
                  className="p-3 rounded-xl glass-hi hover:bg-white/10 hover:border-bx-cyan border border-transparent transition text-center">
                  <div className="font-display font-bold text-sm capitalize">{v}</div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
        {step === 'voice' && (
          <motion.div key="voice" initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="grid grid-cols-3 gap-2">
              {VOICES.map((vc) => (
                <button key={vc} data-testid={`breathe-voice-${vc}`}
                  onClick={() => onVoice(vc)}
                  className="p-3 rounded-xl glass-hi hover:bg-white/10 hover:border-bx-cyan border border-transparent transition text-center">
                  <div className="font-display font-bold text-sm capitalize">{vc}</div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
        {step === 'forming' && (
          <motion.div key="forming" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
            <div className="text-bx-cyan text-sm uppercase tracking-widest animate-pulse">forming…</div>
            <p className="text-white/65 text-xs mt-2">The bubble is being painted. ~15–30s.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Breathe;
